import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import { getWebPageSchema, getBreadcrumbSchema, getArticleSchema } from '@/utils/structuredData';

const categoryNameMap: Record<string, string> = {
  'risk-warning': '风险警示',
  'healthy-alternative': '健康替代',
  'legal-compliance': '法律合规',
  'youth-protection': '青少年保护',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <div className="pt-24 pb-16 text-center">
          <h2 className="font-heading text-2xl text-foreground-900">文章未找到</h2>
          <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-accent-600 hover:text-accent-700 text-sm cursor-pointer">
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const categoryName = categoryNameMap[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);
  const canonicalUrl = `${siteInfo.url}/article/${article.slug}`;

  const jsonLd = [
    getWebPageSchema(article.title, article.excerpt, canonicalUrl),
    getBreadcrumbSchema([
      { name: '首页', item: siteInfo.url },
      { name: categoryName, item: `${siteInfo.url}/category/${article.category}` },
      { name: article.title, item: canonicalUrl },
    ]),
    getArticleSchema(article),
  ];

  return (
    <Layout>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={canonicalUrl}
        ogType="article"
        isArticle
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
        jsonLd={jsonLd}
      />

      <div className="pt-20 bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            <header className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {categoryName}
                </Link>
                <span className="text-foreground-400 text-xs">{article.date}</span>
                <span className="text-foreground-400 text-xs flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 pb-6 border-b border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-user-line text-accent-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-foreground-800 text-sm font-medium">{article.author}</p>
                  <p className="text-foreground-500 text-xs">{siteInfo.name} 专栏作者</p>
                </div>
              </div>
            </header>

            <div
              className="prose-custom text-foreground-800 leading-relaxed text-sm md:text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-foreground-500 text-xs">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2 group-hover:text-accent-600 transition-colors line-clamp-2">
                      {rel.title}
                    </h3>
                    <p className="text-foreground-600 text-xs leading-relaxed mb-3 line-clamp-2">
                      {rel.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-foreground-500 text-xs">
                      <span>{rel.date}</span>
                      <span>·</span>
                      <span>{rel.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}